import { rateCache } from '../cache';
import { generateLiveMockRates } from '../mock-data';
import { MAIN_CURRENCIES } from '../constants';
import type { ExchangeRate } from '../types';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', XOF: 'CFA',
};

function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
}

function buildRates(
  usdToNgn: number,
  crossRates: Record<string, number>,
  invertCross: boolean,
): ExchangeRate[] {
  return MAIN_CURRENCIES.map(code => {
    const rate = code === 'USD'
      ? usdToNgn
      : invertCross
        ? (1 / crossRates[code]) * usdToNgn
        : usdToNgn / crossRates[code];

    const previousRate = rate * (1 - (Math.random() * 0.02 - 0.01));
    const change = ((rate - previousRate) / previousRate) * 100;

    return {
      fromCurrency: code,
      toCurrency: 'NGN',
      rate: Number(rate.toFixed(4)),
      change: Number(change.toFixed(3)),
      timestamp: new Date(),
      symbol: CURRENCY_SYMBOLS[code] ?? code,
    };
  });
}

async function fetchFromExchangeRateAPI(): Promise<ExchangeRate[]> {
  const response = await fetchWithTimeout('https://api.exchangerate-api.com/v4/latest/USD', 5000);
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  const data = await response.json();
  if (!data.rates?.NGN) throw new Error('Missing NGN rate');

  return buildRates(data.rates.NGN, data.rates, true);
}

async function fetchFromFreeForexAPI(): Promise<ExchangeRate[]> {
  const response = await fetchWithTimeout(
    'https://api.fxratesapi.com/latest?base=USD&currencies=NGN,EUR,GBP,CAD,AUD,XOF',
    5000,
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  const data = await response.json();
  if (!data.rates?.NGN) throw new Error('Missing NGN rate');

  return buildRates(data.rates.NGN, data.rates, false);
}

export async function getExchangeRates(): Promise<ExchangeRate[]> {
  const cached = rateCache.get('latest-rates');
  if (cached) return cached;

  const sources = [fetchFromExchangeRateAPI, fetchFromFreeForexAPI];

  for (const fetchFunction of sources) {
    try {
      const rates = await Promise.race([
        fetchFunction(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('API timeout')), 4000),
        ),
      ]);
      if (rates?.length > 0) {
        rateCache.set('latest-rates', rates, 60000);
        return rates;
      }
    } catch (error) {
      console.debug(`${fetchFunction.name} failed:`, error instanceof Error ? error.message : 'Unknown error');
      continue;
    }
  }

  const mockRates = generateLiveMockRates();
  rateCache.set('latest-rates', mockRates, 30000);
  return mockRates;
}
